import { Button, Textarea, TextInput } from "@tremor/react";
import { useState } from "react";
import { X } from "lucide-react";
import { usePlan } from "../../lib/usePlan.js";
import { MAX_MONITORS } from "../../lib/planGate.js";

// Fastest check interval (seconds) each plan is allowed. Mirrors lib/plan_limits.py.
const PLAN_MIN_INTERVAL = { standard: 30, professional: 30 };

const INTERVAL_OPTIONS = [
  { label: "30 seconds", value: 30 },
  { label: "1 minute", value: 60 },
  { label: "5 minutes", value: 300 },
  { label: "10 minutes", value: 600 },
  { label: "30 minutes", value: 1800 },
  { label: "1 hour", value: 3600 },
  { label: "2 hours", value: 7200 },
  { label: "4 hours", value: 14400 },
  { label: "12 hours", value: 43200 },
  { label: "1 day", value: 86400 },
  { label: "1 week", value: 604800 },
  { label: "Custom...", value: 0 },
];

const INTERVAL_PRESET_VALUES = new Set(
  INTERVAL_OPTIONS.filter((o) => o.value > 0).map((o) => o.value)
);

const CUSTOM_UNITS = [
  { label: "minutes", seconds: 60 },
  { label: "hours", seconds: 3600 },
  { label: "days", seconds: 86400 },
  { label: "weeks", seconds: 604800 },
];

function IntervalSelect({ value, onChange }) {
  const plan = usePlan();
  const planMin = PLAN_MIN_INTERVAL[plan] ?? 300;
  const [customMode, setCustomMode] = useState(!INTERVAL_PRESET_VALUES.has(value));

  function decompose(secs) {
    for (const unit of [...CUSTOM_UNITS].reverse()) {
      if (secs % unit.seconds === 0 && secs / unit.seconds >= 1) {
        return { amount: secs / unit.seconds, unit: unit.seconds };
      }
    }
    return { amount: Math.round(secs / 60), unit: 60 };
  }

  const { amount: customAmount, unit: customUnit } = decompose(value);

  return (
    <div className="space-y-2">
      <select
        value={customMode ? 0 : value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v > 0) {
            setCustomMode(false);
            onChange(v);
          } else {
            setCustomMode(true);
          }
        }}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
      >
        {INTERVAL_OPTIONS.map((o) => {
          const locked = o.value > 0 && o.value < planMin;
          return (
            <option key={o.value} value={o.value} disabled={locked}>
              {locked ? `${o.label} — upgrade required` : o.label}
            </option>
          );
        })}
      </select>
      {customMode && (
        <div className="flex gap-2">
          <input
            type="number"
            min={1}
            value={customAmount}
            onChange={(e) => onChange(Math.max(1, Number(e.target.value)) * customUnit)}
            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
          <select
            value={customUnit}
            onChange={(e) => onChange(customAmount * Number(e.target.value))}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            {CUSTOM_UNITS.map((u) => (
              <option key={u.seconds} value={u.seconds}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"];
const METHODS_WITH_BODY = ["POST", "PUT", "PATCH"];

function HttpOptions({ options, onChange }) {
  const method = options.method ?? "GET";
  const showBody = METHODS_WITH_BODY.includes(method);

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        HTTP Options
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Method</label>
        <select
          value={method}
          onChange={(e) => onChange("method", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {HTTP_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {showBody && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Body type
            </label>
            <select
              value={options.body_type ?? "json"}
              onChange={(e) => onChange("body_type", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              <option value="json">JSON</option>
              <option value="form">Form encoded</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Body</label>
            <Textarea
              rows={4}
              value={options.body ?? ""}
              onValueChange={(v) => onChange("body", v)}
              placeholder={
                (options.body_type ?? "json") === "json"
                  ? '{"key": "value"}'
                  : "key=value&other=data"
              }
            />
          </div>
        </>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Timeout (seconds)
        </label>
        <input
          type="number"
          min={1}
          max={60}
          value={options.timeout_seconds ?? 10}
          onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>
    </div>
  );
}

function SslOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        SSL Options
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Warn when &lt; N days
          </label>
          <input
            type="number"
            min={1}
            value={options.warning_days ?? 14}
            onChange={(e) => onChange("warning_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Error when &lt; N days
          </label>
          <input
            type="number"
            min={1}
            value={options.critical_days ?? 3}
            onChange={(e) => onChange("critical_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
    </div>
  );
}

function DomainOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Domain Expiration Options
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Warn when &lt; N days
          </label>
          <input
            type="number"
            min={1}
            value={options.warning_days ?? 30}
            onChange={(e) => onChange("warning_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Error when &lt; N days
          </label>
          <input
            type="number"
            min={1}
            value={options.critical_days ?? 7}
            onChange={(e) => onChange("critical_days", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
    </div>
  );
}

function DnsOptions({ options, onChange }) {
  const expectedRaw = (options.expected_values ?? []).join(", ");
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        DNS Options
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Record type
        </label>
        <select
          value={options.record_type ?? "A"}
          onChange={(e) => onChange("record_type", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {["A", "AAAA", "CNAME", "MX", "TXT"].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Expected values{" "}
          <span className="font-normal text-gray-400">(optional, comma-separated)</span>
        </label>
        <TextInput
          value={expectedRaw}
          onValueChange={(v) =>
            onChange(
              "expected_values",
              v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          placeholder="93.184.216.34, 93.184.216.100"
        />
        <p className="mt-1 text-xs text-gray-400">
          If set, the check fails unless at least one value is returned.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Custom nameserver{" "}
          <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <TextInput
          value={options.nameserver ?? ""}
          onValueChange={(v) => onChange("nameserver", v || undefined)}
          placeholder="8.8.8.8"
        />
      </div>
    </div>
  );
}

function TcpOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        TCP Options
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Port <span className="font-normal text-gray-400">(if not in target)</span>
        </label>
        <input
          type="number"
          min={1}
          max={65535}
          value={options.port ?? ""}
          onChange={(e) =>
            onChange("port", e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder="80"
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">
          You can also include the port directly in the target: host:port
        </p>
      </div>
    </div>
  );
}

const STEP_ACTIONS = [
  { value: "goto", label: "Go to URL" },
  { value: "click", label: "Click element" },
  { value: "fill", label: "Fill input" },
  { value: "wait", label: "Wait" },
  { value: "assert_url", label: "Assert URL contains" },
  { value: "assert_text", label: "Assert element text" },
  { value: "assert_visible", label: "Assert element visible" },
  { value: "screenshot", label: "Screenshot" },
];

function StepRow({ step, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  function field(key, value) {
    onChange({ ...step, [key]: value });
  }
  return (
    <div className="rounded border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="w-5 text-center text-xs font-semibold text-gray-400">
          {index + 1}
        </span>
        <select
          value={step.action}
          onChange={(e) => onChange({ action: e.target.value })}
          className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        >
          {STEP_ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={onMoveUp}
            className="rounded px-1.5 py-1 text-xs text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={onMoveDown}
            className="rounded px-1.5 py-1 text-xs text-gray-400 hover:bg-gray-100 disabled:opacity-30"
          >
            ▼
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded px-1.5 py-1 text-xs text-red-400 hover:bg-red-50"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Action-specific fields */}
      {step.action === "goto" && (
        <input
          placeholder="https://example.com/page"
          value={step.url ?? ""}
          onChange={(e) => field("url", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {(step.action === "click" || step.action === "assert_visible") && (
        <input
          placeholder="CSS selector, e.g. button#submit"
          value={step.selector ?? ""}
          onChange={(e) => field("selector", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {step.action === "fill" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <input
            placeholder="Value to type"
            value={step.value ?? ""}
            onChange={(e) => field("value", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
        </div>
      )}
      {step.action === "wait" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector (optional)"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={100}
              max={30000}
              placeholder="Timeout ms"
              value={step.timeout_ms ?? ""}
              onChange={(e) => field("timeout_ms", Number(e.target.value))}
              className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
            />
            <span className="text-xs text-gray-400">ms</span>
          </div>
        </div>
      )}
      {step.action === "assert_url" && (
        <input
          placeholder="URL must contain this string"
          value={step.contains ?? ""}
          onChange={(e) => field("contains", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
      {step.action === "assert_text" && (
        <div className="space-y-1.5">
          <input
            placeholder="CSS selector"
            value={step.selector ?? ""}
            onChange={(e) => field("selector", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <input
            placeholder="Expected text (substring match)"
            value={step.text ?? ""}
            onChange={(e) => field("text", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          />
        </div>
      )}
      {step.action === "screenshot" && (
        <input
          placeholder="Name (e.g. after-login)"
          value={step.name ?? ""}
          onChange={(e) => field("name", e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
        />
      )}
    </div>
  );
}

function PlaywrightOptions({ options, onChange }) {
  const steps = options.steps ?? [];

  function updateSteps(newSteps) {
    onChange("steps", newSteps.length > 0 ? newSteps : undefined);
  }

  function addStep() {
    updateSteps([...steps, { action: "goto", url: "" }]);
  }

  function updateStep(i, updated) {
    const next = [...steps];
    next[i] = updated;
    updateSteps(next);
  }

  function removeStep(i) {
    updateSteps(steps.filter((_, idx) => idx !== i));
  }

  function moveStep(i, dir) {
    const next = [...steps];
    const j = i + dir;
    [next[i], next[j]] = [next[j], next[i]];
    updateSteps(next);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Browser Options
        </p>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Wait until (simple mode only)
          </label>
          <select
            value={options.wait_until ?? "networkidle"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="networkidle">Network idle (recommended)</option>
            <option value="load">Load event</option>
            <option value="domcontentloaded">DOMContentLoaded</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />

        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={options.full_page ?? false}
            onChange={(e) => onChange("full_page", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
          />
          Capture full-page screenshot (simple mode)
        </label>
      </div>

      {/* Transaction steps */}
      <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">
              Transaction Steps
            </p>
            <p className="mt-0.5 text-xs text-teal-600">
              {steps.length === 0
                ? "No steps — runs as a simple page check."
                : `${steps.length} step${steps.length !== 1 ? "s" : ""} — runs as a multi-step transaction.`}
            </p>
          </div>
          <Button type="button" size="xs" onClick={addStep}>
            + Add Step
          </Button>
        </div>

        {steps.length > 0 && (
          <div className="space-y-2">
            {steps.map((step, i) => (
              <StepRow
                key={i}
                step={step}
                index={i}
                total={steps.length}
                onChange={(updated) => updateStep(i, updated)}
                onRemove={() => removeStep(i)}
                onMoveUp={() => moveStep(i, -1)}
                onMoveDown={() => moveStep(i, 1)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ShopifyOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
        Shopify Storefront Options
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Product URL <span className="font-normal text-gray-400">(recommended)</span>
        </label>
        <TextInput
          value={options.product_url ?? ""}
          onValueChange={(v) => onChange("product_url", v || undefined)}
          placeholder="https://store.example.com/products/example-product"
        />
        <p className="mt-1 text-xs text-gray-400">
          If left blank, Monitero tries to discover a product page automatically.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Collection URL{" "}
          <span className="font-normal text-gray-400">(optional fallback)</span>
        </label>
        <TextInput
          value={options.collection_url ?? ""}
          onValueChange={(v) => onChange("collection_url", v || undefined)}
          placeholder="https://store.example.com/collections/all"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Wait until
          </label>
          <select
            value={options.wait_until ?? "load"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="load">Load event</option>
            <option value="domcontentloaded">DOMContentLoaded</option>
            <option value="networkidle">Network idle</option>
          </select>
        </div>

        <label className="flex items-center gap-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={options.check_checkout ?? true}
            onChange={(e) => onChange("check_checkout", e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
          />
          Verify checkout handoff
        </label>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Viewport</label>
        <select
          value={options.viewport ?? "desktop"}
          onChange={(e) => onChange("viewport", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="desktop">Desktop (1366×900)</option>
          <option value="mobile">Mobile (iPhone Safari, 390×844)</option>
        </select>
        <p className="mt-1 text-xs text-gray-400">
          Mobile uses a real iPhone user agent and touch-enabled viewport.
        </p>
      </div>
    </div>
  );
}

function WordPressOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
        WordPress Monitor Options
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={120}
            value={options.timeout_seconds ?? 30}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Wait until
          </label>
          <select
            value={options.wait_until ?? "networkidle"}
            onChange={(e) => onChange("wait_until", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          >
            <option value="networkidle">Network idle</option>
            <option value="load">Load event</option>
            <option value="domcontentloaded">DOMContentLoaded</option>
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Viewport</label>
        <select
          value={options.viewport ?? "desktop"}
          onChange={(e) => onChange("viewport", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="desktop">Desktop (1366×900)</option>
          <option value="mobile">Mobile (iPhone Safari, 390×844)</option>
        </select>
        <p className="mt-1 text-xs text-gray-400">
          Mobile uses a real iPhone user agent and touch-enabled viewport.
        </p>
      </div>

      <label className="flex items-center gap-2 rounded-md border border-blue-100 bg-white px-3 py-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.check_rest_api ?? true}
          onChange={(e) => onChange("check_rest_api", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Check REST API health
      </label>
    </div>
  );
}

function PingOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Ping Options
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Packets
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={options.count ?? 3}
            onChange={(e) => onChange("count", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={1}
            max={30}
            value={options.timeout_seconds ?? 10}
            onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
          />
        </div>
      </div>
    </div>
  );
}

function PortOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Port Options
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Expected state
        </label>
        <select
          value={options.expected_state ?? "open"}
          onChange={(e) => onChange("expected_state", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          <option value="open">Open — connection should succeed</option>
          <option value="closed">Closed — port should be unreachable</option>
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Port <span className="font-normal text-gray-400">(if not in target)</span>
        </label>
        <input
          type="number"
          min={1}
          max={65535}
          value={options.port ?? ""}
          onChange={(e) =>
            onChange("port", e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder="22"
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>
    </div>
  );
}

const _ASSERTION_OPERATORS = {
  status_code: ["eq", "neq", "lt", "gt", "in"],
  header: ["eq", "neq", "contains", "regex"],
  jsonpath: ["eq", "neq", "contains", "regex"],
  body_regex: [],
};
const _OPERATOR_LABELS = {
  eq: "=",
  neq: "≠",
  lt: "<",
  gt: ">",
  in: "in list",
  contains: "contains",
  regex: "matches regex",
};

function AssertionRow({ assertion, onChange, onRemove }) {
  const atype = assertion.type ?? "status_code";
  const operators = _ASSERTION_OPERATORS[atype] ?? [];
  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-gray-200 bg-white p-2">
      <select
        value={atype}
        onChange={(e) => onChange({ type: e.target.value, operator: "eq", value: "" })}
        className="rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
      >
        <option value="status_code">Status code</option>
        <option value="header">Header</option>
        <option value="jsonpath">JSONPath</option>
        <option value="body_regex">Body regex</option>
      </select>
      {atype === "header" && (
        <input
          type="text"
          placeholder="Header name"
          value={assertion.name ?? ""}
          onChange={(e) => onChange({ ...assertion, name: e.target.value })}
          className="w-36 rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        />
      )}
      {atype === "jsonpath" && (
        <input
          type="text"
          placeholder="$.status"
          value={assertion.path ?? ""}
          onChange={(e) => onChange({ ...assertion, path: e.target.value })}
          className="w-36 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      )}
      {operators.length > 0 && (
        <select
          value={assertion.operator ?? "eq"}
          onChange={(e) => onChange({ ...assertion, operator: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {_OPERATOR_LABELS[op] ?? op}
            </option>
          ))}
        </select>
      )}
      {atype === "body_regex" ? (
        <input
          type="text"
          placeholder="pattern"
          value={assertion.pattern ?? ""}
          onChange={(e) => onChange({ ...assertion, pattern: e.target.value })}
          className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      ) : atype === "status_code" && assertion.operator === "in" ? (
        <input
          type="text"
          placeholder="200, 201, 202"
          value={(assertion.values ?? []).join(", ")}
          onChange={(e) =>
            onChange({
              ...assertion,
              values: e.target.value
                .split(",")
                .map((v) => parseInt(v.trim(), 10))
                .filter((n) => !isNaN(n)),
            })
          }
          className="flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-xs outline-none focus:border-teal-600"
        />
      ) : (
        <input
          type={atype === "status_code" ? "number" : "text"}
          placeholder={atype === "status_code" ? "200" : "expected value"}
          value={assertion.value ?? ""}
          onChange={(e) =>
            onChange({
              ...assertion,
              value: atype === "status_code" ? Number(e.target.value) : e.target.value,
            })
          }
          className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs outline-none focus:border-teal-600"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="ml-auto rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ApiOptions({ options, onChange }) {
  const method = options.method ?? "GET";
  const showBody = METHODS_WITH_BODY.includes(method);
  const assertions = options.assertions ?? [];

  function updateAssertion(idx, updated) {
    onChange(
      "assertions",
      assertions.map((a, i) => (i === idx ? updated : a))
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        API Options
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Method</label>
        <select
          value={method}
          onChange={(e) => onChange("method", e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        >
          {HTTP_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      {showBody && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Body type
            </label>
            <select
              value={options.body_type ?? "json"}
              onChange={(e) => onChange("body_type", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
            >
              <option value="json">JSON</option>
              <option value="form">Form encoded</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Body</label>
            <Textarea
              rows={3}
              value={options.body ?? ""}
              onValueChange={(v) => onChange("body", v)}
              placeholder='{"key": "value"}'
            />
          </div>
        </>
      )}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Timeout (seconds)
        </label>
        <input
          type="number"
          min={1}
          max={60}
          value={options.timeout_seconds ?? 10}
          onChange={(e) => onChange("timeout_seconds", Number(e.target.value))}
          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">Assertions</label>
          <Button
            type="button"
            variant="light"
            size="xs"
            onClick={() =>
              onChange("assertions", [
                ...assertions,
                { type: "status_code", operator: "eq", value: 200 },
              ])
            }
          >
            + Add
          </Button>
        </div>
        {assertions.length === 0 && (
          <p className="text-xs text-gray-400">
            No assertions — any 2xx–3xx response is up.
          </p>
        )}
        <div className="space-y-1.5">
          {assertions.map((a, i) => (
            <AssertionRow
              key={i}
              assertion={a}
              onChange={(updated) => updateAssertion(i, updated)}
              onRemove={() =>
                onChange(
                  "assertions",
                  assertions.filter((_, j) => j !== i)
                )
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CronOptions({ options, onChange }) {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Cron Options
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Cron schedule
        </label>
        <TextInput
          placeholder="*/5 * * * *"
          value={options.cron_schedule ?? ""}
          onValueChange={(v) => onChange("cron_schedule", v || undefined)}
          className="w-48"
        />
        <p className="mt-1 text-xs text-gray-400">
          Standard 5-field cron expression (minute hour day month weekday). Leave blank
          to use the monitor interval instead.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Grace period (seconds)
        </label>
        <input
          type="number"
          min={0}
          value={options.grace_period_seconds ?? 0}
          onChange={(e) => onChange("grace_period_seconds", Number(e.target.value))}
          className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-400">
          Extra time to wait after the scheduled run before marking as down.
        </p>
      </div>
      <div>
        <p className="mb-2 text-xs font-medium text-gray-600">
          Optional assertion{" "}
          <span className="font-normal text-gray-400">
            (query param / JSON body / form field)
          </span>
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Key</label>
            <TextInput
              placeholder="status"
              value={options.assert_key ?? ""}
              onValueChange={(v) => onChange("assert_key", v || undefined)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              Expected value
            </label>
            <TextInput
              placeholder="ok"
              value={options.assert_value ?? ""}
              onValueChange={(v) => onChange("assert_value", v || undefined)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- New advisory monitor option forms -------------------------------------

function OptionsCard({ title, children }) {
  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, min = 0, step = 1, help, placeholder }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type="number"
        min={min}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
      />
      {help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}

function ViewportSelect({ value, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">Viewport</label>
      <select
        value={value ?? "desktop"}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
      >
        <option value="desktop">Desktop (1366×900)</option>
        <option value="mobile">Mobile (iPhone Safari, 390×844)</option>
      </select>
      <p className="mt-1 text-xs text-gray-400">
        Mobile uses a real iPhone user agent and touch-enabled viewport.
      </p>
    </div>
  );
}

function SeoHealthOptions({ options, onChange }) {
  return (
    <OptionsCard title="SEO health">
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.expect_indexable ?? true}
          onChange={(e) => onChange("expect_indexable", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        This page should be indexable (flag noindex)
      </label>
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
    </OptionsCard>
  );
}

function BrokenLinksOptions({ options, onChange }) {
  return (
    <OptionsCard title="Broken links crawl">
      <div className="flex flex-wrap gap-4">
        <NumberField
          label="Max pages"
          value={options.max_pages ?? 50}
          min={1}
          onChange={(v) => onChange("max_pages", v)}
          help="Hard cap 200"
        />
        <NumberField
          label="Max depth"
          value={options.max_depth ?? 3}
          min={1}
          onChange={(v) => onChange("max_depth", v)}
          help="Hard cap 5"
        />
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.check_external ?? true}
          onChange={(e) => onChange("check_external", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Also check external links
      </label>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.respect_robots ?? true}
          onChange={(e) => onChange("respect_robots", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        Respect robots.txt
      </label>
    </OptionsCard>
  );
}

function ImageOptimizationOptions({ options, onChange }) {
  return (
    <OptionsCard title="Image optimization">
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
      <NumberField
        label="Oversized threshold (bytes)"
        value={options.max_image_bytes ?? 200000}
        min={1000}
        step={10000}
        onChange={(v) => onChange("max_image_bytes", v)}
        help="Flag images transferred above this size (default 200KB)."
      />
    </OptionsCard>
  );
}

const BUDGET_FIELDS = [
  { key: "max_lcp_ms", label: "Max LCP (ms)", step: 100 },
  { key: "max_cls", label: "Max CLS", step: 0.01, min: 0 },
  { key: "max_ttfb_ms", label: "Max TTFB (ms)", step: 50 },
  { key: "max_total_weight_bytes", label: "Max total weight (bytes)", step: 100000 },
  { key: "max_js_bytes", label: "Max JS (bytes)", step: 50000 },
  { key: "max_image_bytes", label: "Max images (bytes)", step: 50000 },
  { key: "max_requests", label: "Max requests" },
  { key: "max_failed_requests", label: "Max failed requests" },
];

function PerformanceBudgetOptions({ options, onChange }) {
  const budgets = options.budgets ?? {};
  const setBudget = (key, v) => {
    const next = { ...budgets };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange("budgets", next);
  };
  return (
    <OptionsCard title="Performance budgets">
      <p className="text-xs text-gray-500">
        Leave a field blank to skip that budget. A breach marks the check degraded.
      </p>
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
      <div className="flex flex-wrap gap-4">
        {BUDGET_FIELDS.map((f) => (
          <NumberField
            key={f.key}
            label={f.label}
            value={budgets[f.key]}
            min={f.min ?? 0}
            step={f.step ?? 1}
            onChange={(v) => setBudget(f.key, v)}
          />
        ))}
      </div>
    </OptionsCard>
  );
}

function SuspiciousScriptOptions({ options, onChange }) {
  const hosts = (options.allowed_script_hosts ?? []).join("\n");
  return (
    <OptionsCard title="Suspicious script detection">
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Approved script hosts (one per line)
        </label>
        <textarea
          rows={4}
          value={hosts}
          placeholder={"www.googletagmanager.com\ncdn.example.com"}
          onChange={(e) =>
            onChange(
              "allowed_script_hosts",
              e.target.value
                .split("\n")
                .map((h) => h.trim())
                .filter(Boolean)
            )
          }
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
        />
        <p className="mt-1 text-xs text-gray-500">
          When set, any external script host not on this list is flagged as a new,
          unapproved host.
        </p>
      </div>
    </OptionsCard>
  );
}

function CarbonFootprintOptions({ options, onChange }) {
  return (
    <OptionsCard title="Carbon footprint">
      <ViewportSelect value={options.viewport} onChange={(v) => onChange("viewport", v)} />
      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={options.green_host ?? false}
          onChange={(e) => onChange("green_host", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
        />
        This site is hosted on renewable energy
      </label>
    </OptionsCard>
  );
}

export {
  INTERVAL_OPTIONS,
  INTERVAL_PRESET_VALUES,
  CUSTOM_UNITS,
  IntervalSelect,
  HttpOptions,
  SeoHealthOptions,
  BrokenLinksOptions,
  ImageOptimizationOptions,
  PerformanceBudgetOptions,
  SuspiciousScriptOptions,
  CarbonFootprintOptions,
  SslOptions,
  DomainOptions,
  DnsOptions,
  TcpOptions,
  STEP_ACTIONS,
  StepRow,
  PlaywrightOptions,
  ShopifyOptions,
  WordPressOptions,
  PingOptions,
  PortOptions,
  AssertionRow,
  ApiOptions,
  CronOptions,
  ViewportSelect,
};
